import * as Slot from "@rn-primitives/slot"
import type { SlottableViewProps } from "@rn-primitives/types"
import { cva, type VariantProps } from "class-variance-authority"
import { View } from "react-native"
import { cn } from "@/lib/utils"
import { TextClassContext } from "@/components/reusables/ui/text"
import { BFTextVariants } from "@/components/standard/BFText"

const badgeVariants = cva(
  "web:inline-flex items-center rounded-sm border border-border px-1.5 py-0.5 web:transition-colors web:focus:outline-none web:focus:ring-2 web:focus:ring-ring web:focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary web:hover:opacity-80 active:opacity-80",
        secondary:
          "border-transparent bg-secondary web:hover:opacity-80 active:opacity-80",
        destructive:
          "border-transparent bg-destructive web:hover:opacity-80 active:opacity-80",
        success:
          "border-transparent bg-success web:hover:opacity-80 active:opacity-80",
        warning:
          "border-transparent bg-warning web:hover:opacity-80 active:opacity-80",
        muted:
          "border-transparent bg-muted web:hover:opacity-80 active:opacity-80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const badgeTextVariants = cva(BFTextVariants.H7, {
  variants: {
    variant: {
      default: "text-primary-foreground",
      secondary: "text-secondary-foreground",
      destructive: "text-destructive-foreground",
      success: "text-success-foreground",
      warning: "text-warning-foreground",
      muted: "text-muted-foreground",
      outline: "text-foreground",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

type BadgeProps = SlottableViewProps & VariantProps<typeof badgeVariants>

function Badge({ className, variant, asChild, ...props }: BadgeProps) {
  const Component = asChild ? Slot.View : View
  return (
    <TextClassContext.Provider value={badgeTextVariants({ variant })}>
      <Component
        className={cn(
          "flex-row items-center gap-1",
          badgeTextVariants({ variant }),
          badgeVariants({ variant }),
          className
        )}
        {...props}
      />
    </TextClassContext.Provider>
  )
}

export { Badge, badgeTextVariants, badgeVariants }
export type { BadgeProps }
